// 全局设置页。launcher policy 是 daemon 级默认值；machine cache policy 只做显式覆盖。

import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { apiGet, apiPostForm, APIError } from "@/api/client";
import { launcherPolicyPath } from "@/api/endpoints";
import type { LauncherPolicyResponse } from "@/api/types";
import { QueryView } from "@/components/ui/QueryView";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Switch } from "@/components/ui/Switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { Badge } from "@/components/ui/Badge";
import { useSession } from "@/hooks/useSession";
import { toast } from "@/components/ui/Sonner";
import { formatMs } from "@/lib/format";

export const Route = createFileRoute("/settings")({
  component: SettingsRoute,
});

function SettingsRoute() {
  const session = useSession();
  const canWrite = session.data?.can_write ?? false;
  const q = useQuery({
    queryKey: ["launcher-policy"],
    queryFn: () => apiGet<LauncherPolicyResponse>(launcherPolicyPath),
  });

  return (
    <div className="space-y-4">
      <QueryView query={q}>
        {(data) => <LauncherPolicyForm data={data} canWrite={canWrite} />}
      </QueryView>
    </div>
  );
}

interface LauncherPolicyFormProps {
  data: LauncherPolicyResponse;
  canWrite: boolean;
}

function LauncherPolicyForm({ data, canWrite }: LauncherPolicyFormProps) {
  const queryClient = useQueryClient();
  const policy = data.policy;
  const [luaSealed, setLuaSealed] = useState(policy.lua_sealed_stub_enabled);
  const [socketDev, setSocketDev] = useState(policy.socket_dev_modules_enabled);
  const [stubVersion, setStubVersion] = useState(policy.stub_version ?? "");
  const [bootstrapMode, setBootstrapMode] = useState(
    policy.bootstrap_mode || "memory_socket",
  );
  const [daemonHost, setDaemonHost] = useState(policy.daemon_lua_host ?? "");
  const [daemonPort, setDaemonPort] = useState(
    policy.daemon_lua_port ? String(policy.daemon_lua_port) : "8173",
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLuaSealed(policy.lua_sealed_stub_enabled);
    setSocketDev(policy.socket_dev_modules_enabled);
    setStubVersion(policy.stub_version ?? "");
    setBootstrapMode(policy.bootstrap_mode || "memory_socket");
    setDaemonHost(policy.daemon_lua_host ?? "");
    setDaemonPort(policy.daemon_lua_port ? String(policy.daemon_lua_port) : "8173");
  }, [policy]);

  async function save() {
    setSaving(true);
    try {
      await apiPostForm(launcherPolicyPath, {
        lua_sealed_stub_enabled: luaSealed ? "1" : "0",
        socket_dev_modules_enabled: socketDev ? "1" : "0",
        stub_version: stubVersion,
        bootstrap_mode: bootstrapMode,
        daemon_lua_host: daemonHost,
        daemon_lua_port: daemonPort,
      });
      toast.success("已保存全局 launcher policy");
      void queryClient.invalidateQueries({ queryKey: ["launcher-policy"] });
      void queryClient.invalidateQueries({ queryKey: ["machine-caches"] });
      void queryClient.invalidateQueries({ queryKey: ["machine"] });
    } catch (err) {
      const msg =
        err instanceof APIError
          ? err.message
          : err instanceof Error
            ? err.message
            : String(err);
      toast.error("保存失败", { description: msg });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between gap-3">
        <div>
          <CardTitle>Launcher / cache 默认策略</CardTitle>
          <p className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">
            机器 cache 未设置覆盖时，会继承这里的 daemon 级默认值。
          </p>
        </div>
        <Badge variant={data.exists ? "secondary" : "outline"}>
          {data.exists ? "已配置" : "未配置"}
        </Badge>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid gap-4 lg:grid-cols-2">
          <ToggleRow
            title="sealed stub"
            description="apply 时安装 launcher memory stub"
            checked={luaSealed}
            disabled={!canWrite}
            onCheckedChange={setLuaSealed}
          />
          <ToggleRow
            title="socket dev modules"
            description="启动期通过 8173 拉取内存模块包"
            checked={socketDev}
            disabled={!canWrite}
            onCheckedChange={setSocketDev}
          />
        </div>

        <div className="grid gap-3 lg:grid-cols-4">
          <label className="space-y-1">
            <span className="text-xs text-[hsl(var(--muted-foreground))]">
              stub_version
            </span>
            <Input
              value={stubVersion}
              onChange={(event) => setStubVersion(event.target.value)}
              disabled={!canWrite}
              placeholder="latest 或 sealed_cache 版本"
            />
          </label>
          <label className="space-y-1">
            <span className="text-xs text-[hsl(var(--muted-foreground))]">
              bootstrap_mode
            </span>
            <Select
              value={bootstrapMode}
              onValueChange={setBootstrapMode}
              disabled={!canWrite}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="memory_socket">memory_socket</SelectItem>
                <SelectItem value="file_dev">file_dev</SelectItem>
              </SelectContent>
            </Select>
          </label>
          <label className="space-y-1">
            <span className="text-xs text-[hsl(var(--muted-foreground))]">
              daemon_lua_host
            </span>
            <Input
              value={daemonHost}
              onChange={(event) => setDaemonHost(event.target.value)}
              disabled={!canWrite}
              placeholder="留空则由 .devbootstrap/agent 推导"
            />
          </label>
          <label className="space-y-1">
            <span className="text-xs text-[hsl(var(--muted-foreground))]">
              daemon_lua_port
            </span>
            <Input
              value={daemonPort}
              onChange={(event) => setDaemonPort(event.target.value)}
              disabled={!canWrite}
              placeholder="8173"
            />
          </label>
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-[hsl(var(--border))] pt-4">
          <div className="text-xs text-[hsl(var(--muted-foreground))]">
            {policy.updated_at
              ? `上次保存：${formatMs(policy.updated_at)}`
              : "尚未保存全局策略"}
          </div>
          <Button
            type="button"
            disabled={!canWrite || saving}
            onClick={() => void save()}
          >
            {saving ? "保存中..." : "保存全局策略"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

interface ToggleRowProps {
  title: string;
  description: string;
  checked: boolean;
  disabled: boolean;
  onCheckedChange: (checked: boolean) => void;
}

function ToggleRow({
  title,
  description,
  checked,
  disabled,
  onCheckedChange,
}: ToggleRowProps) {
  return (
    <div className="flex items-center justify-between gap-3 rounded border border-[hsl(var(--border))] p-3">
      <div className="min-w-0">
        <div className="text-sm font-medium">{title}</div>
        <div className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">
          {description}
        </div>
      </div>
      <Switch
        checked={checked}
        disabled={disabled}
        onCheckedChange={onCheckedChange}
        aria-label={title}
      />
    </div>
  );
}
